import React from 'react';
import {
  Leaf, BarChart3, FileText, Shield, Bot, Users, Package, Recycle,
  Zap, Droplets, Building2, Factory, Landmark, TreePine, ClipboardCheck,
  ScrollText, Target, Layers, GitBranch, Gauge, MessageSquare, Award,
  Wallet, HeartHandshake,
} from 'lucide-react';
import ModuleGrid from '../shared/ModuleGrid';

/**
 * All ESGCaffe modules presented as a hover-scaling bento grid.
 * Grouped by domain (Environmental / Social / Governance / Platform) but
 * rendered flat for a punchy "look at everything we do" impression.
 */
const modules = [
  // ── Environmental ──────────────────────────────────────────────
  { id: 'carbon',        label: 'Carbon Accounting',       description: 'Scope 1, 2, 3 emissions across every activity.',        icon: Leaf,          accent: '#10b981' },
  { id: 'ghg',           label: 'GHG Inventory',           description: 'Corporate greenhouse gas inventory and forecasts.',    icon: Factory,       accent: '#059669' },
  { id: 'energy',        label: 'Energy Management',       description: 'Track electricity, fuel, and renewables in one place.', icon: Zap,          accent: '#f59e0b' },
  { id: 'water',         label: 'Water Management',        description: 'Withdrawal, discharge, and stress-region intensity.',   icon: Droplets,      accent: '#06b6d4' },
  { id: 'waste',         label: 'Waste & Circularity',     description: 'Waste streams, diversion rates, circular metrics.',    icon: Recycle,       accent: '#84cc16' },
  { id: 'lca',           label: 'Life Cycle Assessment',   description: 'Cradle-to-grave LCA with ISO 14040/44 methodology.',   icon: Layers,        accent: '#8b5cf6' },
  { id: 'pcf',           label: 'Product Carbon Footprint', description: 'Product-level footprints for customer disclosure.',   icon: Package,       accent: '#14b8a6' },
  { id: 'biodiversity',  label: 'Biodiversity & Nature',   description: 'TNFD-aligned nature-related risk and impact.',         icon: TreePine,      accent: '#22c55e' },

  // ── Social ─────────────────────────────────────────────────────
  { id: 'social',        label: 'Social & DEI',            description: 'Workforce, diversity, safety, human rights metrics.',  icon: Users,         accent: '#ec4899' },
  { id: 'suppliers',     label: 'Supplier Management',     description: 'One-click PCF and Scope 3 collection from suppliers.', icon: GitBranch,     accent: '#f97316' },
  { id: 'stakeholder',   label: 'Stakeholder Surveys',     description: 'Materiality surveys with token-based public links.',   icon: HeartHandshake, accent: '#e11d48' },
  { id: 'communication', label: 'Sustainability Comms',    description: 'Publish updates, ratings, and narratives externally.', icon: MessageSquare, accent: '#f43f5e' },

  // ── Governance & Frameworks ────────────────────────────────────
  { id: 'csrd',          label: 'CSRD / ESRS',             description: '~93 disclosure questions gated by double materiality.', icon: ScrollText,    accent: '#3b82f6' },
  { id: 'aasb-s2',       label: 'AASB S2 / IFRS S2',       description: '4-pillar climate disclosures with risk register.',    icon: Shield,        accent: '#0ea5e9' },
  { id: 'tcfd',          label: 'TCFD',                    description: 'Governance, strategy, risk, metrics & targets.',       icon: ClipboardCheck, accent: '#1d4ed8' },
  { id: 'gri',           label: 'GRI Standards',           description: 'Universal + topic-specific GRI reporting.',            icon: FileText,      accent: '#2563eb' },
  { id: 'eu-taxonomy',   label: 'EU Taxonomy',             description: '6 objectives · 25 activities · turnover/capex/opex.',  icon: Landmark,      accent: '#4f46e5' },
  { id: 'materiality',   label: 'Double Materiality',      description: 'IRO scoring, financial + impact heatmaps.',            icon: Target,        accent: '#7c3aed' },
  { id: 'green-cert',    label: 'Green Certification',     description: 'LEED, BREEAM, GRESB certification workflows.',         icon: Award,         accent: '#059669' },
  { id: 'green-loan',    label: 'Green Loan Book',         description: 'Sustainable finance taxonomy & loan tagging.',         icon: Wallet,        accent: '#0d9488' },
  { id: 'assurance',     label: 'Assurance & Audit',       description: 'Auditor workspace with evidence trails.',              icon: Building2,     accent: '#475569' },

  // ── Platform ───────────────────────────────────────────────────
  { id: 'ai-chat',       label: 'AI Assistant',            description: 'In-app copilot that drafts and cites disclosures.',    icon: Bot,           accent: '#a855f7' },
  { id: 'analytics',     label: 'Analytics & Dashboards',  description: 'Live KPI dashboards, benchmarks, drill-down.',         icon: BarChart3,     accent: '#0891b2' },
  { id: 'reporting',     label: 'Reporting Engine',        description: 'PDF, Word, XBRL, Excel, PowerPoint exports.',          icon: Gauge,         accent: '#0369a1' },
];

const CapabilitiesSection = () => {
  return (
    <section className="py-24 md:py-32 bg-white">
      <div className="max-w-6xl mx-auto px-6 mb-12">
        <div className="text-xs font-bold uppercase tracking-[0.28em] text-blue-600 mb-3">
          What you get
        </div>
        <h2 className="text-4xl md:text-6xl font-extrabold tracking-tight text-slate-900">
          20+ modules.{' '}
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-emerald-500">
            One platform.
          </span>
        </h2>
        <p className="mt-4 max-w-2xl text-slate-500 text-lg">
          Every framework, every metric, every stakeholder — covered end-to-end.
          Hover any module to pause and inspect.
        </p>
      </div>

      <div className="w-full">
        <ModuleGrid modules={modules} speed={38} size={96} />
      </div>
    </section>
  );
};

export default CapabilitiesSection;
